/**
 * @file ShopDashboardRecentOrders.tsx
 * @description «Последние заказы» на «Сводке» (phase19.31, разбор A макет
 * (г); SC46, SC69).
 *
 * Пять последних заказов теми же ячейками, что реестр (номер с табличными
 * цифрами, стадия пилюлей по виду стадии, «1 175,00 ₽»), но в одну линию:
 * номер · клиент · стадия · дата · сумма. Строка — ссылка на заказ. На
 * телефоне — компактный список по тем же колонкам. Справа от заголовка —
 * «Все заказы →».
 */

import { ChevronRight, Package } from "lucide-react";
import { Link } from "react-router";

import { OrderStagePill } from "../orders/OrderStagePill";
import { ResponsiveList } from "../ui/ResponsiveList";
import type { ListColumn } from "../ui/responsive-list-types";
import type { OrderDto } from "../../lib/api-orders";
import { orderMoney, orderShortDate } from "../../lib/order-registry-cells";

/** Колонки — подмножество реестра заказов, без второй линии. */
const RECENT_COLUMNS: ListColumn<OrderDto>[] = [
  {
    key: "order",
    header: "Заказ",
    width: "w-[144px]",
    cell: (o) => <span className="whitespace-nowrap font-medium tabular-nums">{o.number}</span>,
    compact: "title",
  },
  {
    key: "client",
    header: "Клиент",
    cell: (o) => <span className="font-medium">{o.customerDisplayName ?? "Без имени"}</span>,
    compact: "subtitle",
    compactCell: (o) => o.customerDisplayName ?? "Без имени",
  },
  {
    key: "stage",
    header: "Стадия",
    width: "w-[200px]",
    cell: (o) => <OrderStagePill order={o} />,
    compact: "trailing",
    compactCell: (o) => <OrderStagePill order={o} className="max-w-[9rem]" />,
  },
  {
    key: "date",
    header: "Дата",
    width: "w-[96px]",
    cell: (o) => <span className="tabular-nums">{orderShortDate(o.createdAt)}</span>,
    compact: "meta",
    compactCell: (o) => orderShortDate(o.createdAt),
  },
  {
    key: "sum",
    header: "Сумма",
    width: "w-[120px]",
    align: "end",
    cell: (o) => <span className="whitespace-nowrap font-semibold tabular-nums">{orderMoney(o)}</span>,
    compact: "meta",
    compactCell: (o) => <span className="whitespace-nowrap font-semibold tabular-nums">{orderMoney(o)}</span>,
  },
];

interface Props {
  orders: OrderDto[] | null;
  error: boolean;
  onRetry: () => void;
  /** База маршрута заказов: `/shop/:slug/orders`. */
  ordersBase: string;
}

export function ShopDashboardRecentOrders({ orders, error, onRetry, ordersBase }: Props) {
  return (
    <section aria-labelledby="dashboard-recent" className="flex min-w-0 flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <h2 id="dashboard-recent" className="type-section-title text-on-surface">
          Последние заказы
        </h2>
        <Link
          to={ordersBase}
          className="type-body inline-flex min-h-[var(--control-compact-h,3rem)] shrink-0 items-center gap-1 rounded-md px-1 font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
        >
          Все заказы
          <ChevronRight aria-hidden className="size-4" />
        </Link>
      </div>
      <ResponsiveList
        aria-label="Последние заказы"
        rows={orders ?? []}
        columns={RECENT_COLUMNS}
        rowKey={(o) => o.id}
        rowHref={(o) => `${ordersBase}/${o.id}`}
        rowLabel={(o) => `Заказ ${o.number}`}
        loading={orders === null && !error}
        error={error ? "Не удалось загрузить последние заказы" : null}
        onRetry={onRetry}
        skeletonRows={5}
        empty={{
          icon: Package,
          title: "Заказов пока нет",
          description: "Они появятся, когда покупатель оформит заказ на сайте.",
        }}
      />
    </section>
  );
}
